// import node_modules
import * as PropTypes from 'prop-types';
import * as React from 'react';
import ReactDOMServer from 'react-dom/server';
import striptags from 'striptags';
import { Heading, Spacer, Text } from '@storylinerlabs/design';
import { MDXRemote } from 'next-mdx-remote';

// import types
import type { MDXRemoteSerializeResult } from 'next-mdx-remote';

// import component
import BlogLayout from '../layouts/BlogLayout';
import Column from '../atoms/Column';
import NewsletterSection from '../elements/NewsletterSection';
import PostsGrid from '../grids/PostsGrid';
import SEO from '../meta/SEO';
import Section from '../atoms/Section';

// import config
import { storylinerRootDomain } from '../../../config';

// define page
const BlogTagTemplate: React.FunctionComponent<{
  blogTag: { content: MDXRemoteSerializeResult; slug: string; title: string };
  blogTags: { slug: string; title: string }[];
  blogPosts: { image: string; slug: string; tags: string[]; title: string }[];
  hrefLang: string;
}> = ({ blogTags, blogPosts, blogTag, hrefLang }) => {
  // init render
  return (
    <>
      <SEO
        description={striptags(
          ReactDOMServer.renderToString(
            <MDXRemote {...(blogTag?.content as MDXRemoteSerializeResult)} />,
          ),
        )}
        hrefLang={hrefLang}
        image={`${storylinerRootDomain}/img/screensavers/screensaver-break-creative-barriers-storyliner.jpg`}
        title={`${blogTag?.title} - Storyliner`}
      />

      <BlogLayout blogTags={blogTags} hrefLang={hrefLang}>
        {/* Hero  */}
        <Section bgColor="desertStorm">
          {/* Navbar */}
          <Spacer height="64px" />
          <Spacer mobileHeight="26px" tabletHeight="36px" height="56px" />
        </Section>
        <Section bgColor="desertStorm">
          <Column cols="lg">
            <Heading alignment="center" tag="h1">
              {`<span class="has-marker">${blogTag?.title}</span>`}
            </Heading>
            <Text alignment="center" size="large">
              {ReactDOMServer.renderToString(
                <MDXRemote {...(blogTag?.content as MDXRemoteSerializeResult)} />,
              )}
            </Text>
          </Column>
        </Section>
        <Section bgColor="desertStorm">
          <Spacer mobileHeight="25px" tabletHeight="25px" height="50px" />
        </Section>

        {/* Blog posts  */}
        <Section>
          <Spacer mobileHeight="25px" tabletHeight="50px" height="50px" />
        </Section>
        <PostsGrid bgColor="white" blogPosts={blogPosts} />
        <Section>
          <Spacer mobileHeight="25px" tabletHeight="50px" height="75px" />
        </Section>

        {/* Newsletter */}
        <Section bgColor="energyYellow">
          <Spacer mobileHeight="25px" tabletHeight="50px" height="75px" />
        </Section>
        <NewsletterSection bgColor="energyYellow" />
        <Section bgColor="energyYellow">
          <Spacer mobileHeight="25px" tabletHeight="50px" height="75px" />
        </Section>
        <Section>
          <Spacer height="50px" />
        </Section>
      </BlogLayout>
    </>
  );
};

BlogTagTemplate.propTypes = {
  blogPosts: PropTypes.arrayOf(
    PropTypes.shape({
      image: PropTypes.string.isRequired,
      slug: PropTypes.string.isRequired,
      tags: PropTypes.arrayOf(PropTypes.string.isRequired).isRequired,
      title: PropTypes.string.isRequired,
    }).isRequired,
  ).isRequired,
  blogTag: PropTypes.shape({
    content: PropTypes.shape({ compiledSource: PropTypes.string.isRequired }).isRequired,
    slug: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
  }).isRequired,
  blogTags: PropTypes.arrayOf(
    PropTypes.shape({
      slug: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
    }).isRequired,
  ).isRequired,
  hrefLang: PropTypes.string.isRequired,
};

export default BlogTagTemplate;
